'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useRouter, usePathname } from 'next/navigation';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { REIMBURSEMENT_PROFILE_SHOW } from 'src/components/PermissionGuard/checks';

type Option = {
  value: string;
  label: string;
  description: string;
};

const baseOptions: Option[] = [
  {
    value: 'total-volume',
    label: 'Total Volume',
    description:
      'Compare procedure volumes across multiple code sets to identify which represent the largest opportunities in your territory.',
  },
  {
    value: 'trends',
    label: 'Volume Trends',
    description:
      'Track how procedure volume has changed over time to spot growing or declining opportunities.',
  },
  {
    value: 'payer-mix',
    label: 'Payer Mix',
    description:
      'Breakdown the distribution of procedures by payer type to understand reimbursement dynamics and target accounts accordingly.',
  },
  {
    value: 'average-sale-price',
    label: 'Opportunity $ (ASP)',
    description:
      'Calculate the total revenue opportunity by combining procedure volume with average sales price to prioritize by dollar value.',
  },
  {
    value: 'volume-by-code',
    label: 'Volume by Code',
    description:
      'Break a single code set out into one column per code to see which individual procedures or diagnoses drive its volume.',
  },
];

const siteOfCareOption: Option = {
  value: 'site-of-care',
  label: 'Site of Care',
  description:
    'Understand where procedures are being performed by care setting to prioritize outreach to hospitals, ASCs, offices, and more.',
};

/**
 * Gated on the same permission that hides reimbursement data on profiles
 * (MKT-641), so companies and users without it never see the insight offered.
 */
const reimbursementOption: Option = {
  value: 'average-reimbursement',
  label: 'Average Reimbursement',
  description:
    'Compare average reimbursement by payer type to identify reimbursement patterns and opportunities.',
};

const AnalyticsInsightDropdown = () => {
  const router = useRouter();
  const pathname = usePathname();
  const auth = useAuthorization();

  const canViewReimbursement = auth.hasPermission(REIMBURSEMENT_PROFILE_SHOW);

  const options = useMemo<Option[]>(
    () =>
      [
        ...baseOptions,
        siteOfCareOption,
        ...(canViewReimbursement ? [reimbursementOption] : []),
      ].sort((a, b) => a.label?.localeCompare(b.label)),
    [canViewReimbursement]
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);

  // Extract the current view from pathname (e.g., /analytics/trends -> 'trends')
  const currentView = pathname.split('/analytics/')[1] || 'trends';
  const selectedOption = options.find((option) => option.value === currentView);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      // Check if there's enough space below
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = options.length * 40 + 8; // Approximate height
      const spaceBelow = viewportHeight - buttonRect.bottom;

      setOpenUpward(spaceBelow < dropdownHeight);
    }
    setIsOpen(!isOpen);
  };

  const handlePrefetch = (optionValue: string) => {
    router.prefetch(`/analytics/${optionValue}`);
  };

  const handleSelect = (optionValue: string) => {
    setIsOpen(false);
    router.push(`/analytics/${optionValue}`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex-1 flex justify-center items-center gap-2.5">
      <div
        className="relative inline-flex flex-col justify-start items-start gap-1"
        ref={dropdownRef}
      >
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className="px-4 py-2 bg-white rounded-full border border-neutral-200 inline-flex justify-start items-center gap-1 hover:bg-neutral-50 transition-colors w-56"
        >
          <div className="pr-2 flex justify-start items-center gap-1.5 flex-1 min-w-0">
            <div className="justify-start text-primary text-sm font-medium font-['Inter'] leading-5 truncate">
              {selectedOption ? selectedOption.label : 'Select insight type...'}
            </div>
          </div>
          <div className="flex justify-center items-center w-5 h-5">
            <Icon
              icon={faCaretDown}
              className={`transition-all duration-300 text-secondary ${
                isOpen ? '-rotate-90' : 'rotate-0'
              }`}
            />
          </div>
        </button>

        {isOpen && (
          <div
            className={`w-56 p-4 left-0 absolute bg-white rounded-lg shadow-sm border border-neutral-200 inline-flex flex-col justify-start items-start gap-4 z-50 ${
              openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            {options.map((option) => {
              const isSelected = option.value === currentView;
              return (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => handlePrefetch(option.value)}
                  className="self-stretch inline-flex justify-between items-center gap-3 hover:opacity-80 transition-opacity"
                >
                  <div className="flex justify-start items-center gap-2">
                    <div
                      className={`justify-start text-xs font-['Inter'] leading-4 whitespace-nowrap ${
                        isSelected
                          ? 'text-sky-600 font-semibold'
                          : 'text-secondary font-normal'
                      }`}
                    >
                      {option.label}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-4 h-4 flex items-center justify-center">
                      <Icon
                        icon={faCheck}
                        size="xs"
                        className="w-3 h-2.5 text-sky-600"
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex-1 flex justify-start items-center gap-1">
        <div className="flex-1 justify-start text-secondary text-sm font-normal font-['Inter'] leading-5 line-clamp-1">
          {selectedOption ? selectedOption.description : ''}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsInsightDropdown;
